import styled from "@emotion/styled";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { ttNormsFontFamily } from "../../../fonts/fonts";
import { MakeOptional } from "../../../util/MakeOptional";
import { createFocusRingStyles } from "../../../util/createFocusRingStyles";
import { withFocusToggleClassName } from "../../../util/withFocusClassName";
import { avatarTheme } from "../avatarTheme";
import { SizeTypes } from "../types";

interface AvatarRootProps {
    showRing: boolean;
    size: SizeTypes;
    themeMode: RequiredThemeKeys;
}

const cssRoundSupported = typeof CSS !== "undefined" && CSS.supports && CSS.supports("width", "round(1px, 1px)");

export const AvatarRoot = withFocusToggleClassName(styled("div")<AvatarRootProps>`
    // If the --sds--avatar--size is not set, this calc will be invalid, allowing the var() for
    // font-size to use the default value.
    --sds--avatar--calculated--font-size: ${cssRoundSupported
        ? "round(calc(var(--sds--avatar--size) / 2.67), 1px)"
        : "calc(var(--sds--avatar--size) / 2.67)"};
    display: inline-flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    flex-grow: 0;
    height: var(--sds--avatar--size, ${getAvatarSizeFromProps}px);
    width: var(--sds--avatar--size, ${getAvatarSizeFromProps}px);
    color: var(--smar--lc--avatar--text-color);

    background: var(--smar--lc--avatar--background);
    :hover {
        background-color: var(--smar--lc--avatar--background--hover);
    }
    background-size: cover;

    font-size: var(--sds--avatar--calculated--font-size, ${getFontSizeFromProps}px);
    // We do want the variable font to be used, but if it's not available, fall back to the old one.
    font-family: "TT Norms Pro var", ${ttNormsFontFamily};
    font-weight: ${({ themeMode }) => avatarTheme.weight(themeMode)};

    white-space: nowrap;

    box-sizing: border-box;
    border-radius: 50%;
    border-style: solid;
    border-color: var(--smar--lc--avatar--border-color);
    border-width: ${getBorderSize}px;

    cursor: default;

    ${(props) =>
        createFocusRingStyles({
            theme: props.themeMode,
            gap: avatarTheme.focusRingGap(props.themeMode),
            componentBorderWidth: Math.floor(getBorderSize(props)),
            componentRadius: "50%",
        })};
`);
AvatarRoot.displayName = "Container";

type SizeMap = {
    [key in SizeTypes]: number;
};

const textSizeMap: SizeMap = {
    xxSmall: 10,
    xSmall: 10,
    small: 10,
    medium: 12,
    large: 18,
    xLarge: 24,
    xxLarge: 36,
};

function getFontSizeFromProps({ size }: AvatarRootProps): number {
    return textSizeMap[size];
}

const avatarSizeMap: SizeMap = {
    xxSmall: 20,
    xSmall: 24,
    small: 28,
    medium: 32,
    large: 48,
    xLarge: 64,
    xxLarge: 96,
};

function getAvatarSizeFromProps({ size }: AvatarRootProps): number {
    return avatarSizeMap[size];
}

// Just using this nice li'l utility type from our boi Matt Pocock to make the types read nicer for
// our users. This deprecation is already confusing enough...
type PrettyIt<T> = {
    [key in keyof T]: T[key];
    // eslint-disable-next-line @typescript-eslint/ban-types
} & {};

//TODO: V5-remove, all instances (faux overloads) of the avatarSizes function can be removed,
//the function isn't used internally to lodestar-core, and has been deprecated for a while
//users of the Avatar component will pass in a required size prop
/**
 *
 * @param size Given a size, returns the size in pixels of the avatar.
 */
export function avatarSizes(size: SizeTypes): number; //TODO: V5-remove

/**
 *
 * @deprecated please don't use this. It was accidentally exposed. I'm sorry, proofing devs :(
 */
export function avatarSizes(config: PrettyIt<MakeOptional<AvatarRootProps, "showRing" | "themeMode">>): number; //TODO: V5-remove

/**
 * This is used in a few places, but it's not terribly useful and it'd be nice to get rid of it.
 * Unfortunately, it's exposed on the lodestar-core API.
 *
 * It has this weird signature because we accidentally change the API and some packages started
 * using it after the change. Packages using it before the change still had the old signature. We'll
 * support both until we can remove it so we don't break anyone else.
 *
 * @deprecated please don't use this. Just pass in a size  //TODO: V5-remove
 */
export function avatarSizes(sizeOrObj: SizeTypes | MakeOptional<AvatarRootProps, "showRing" | "themeMode">): number {
    if (typeof sizeOrObj === "string") {
        return avatarSizeMap[sizeOrObj];
    } else {
        return avatarSizeMap[sizeOrObj.size];
    }
}

function getBorderSize({ size, showRing }: AvatarRootProps): number {
    if (!showRing) {
        return 0;
    }
    switch (size) {
        case "xxSmall":
            return 1;
        case "xSmall":
            return 1.5;
        case "small":
            return 2;
        case "medium":
        default:
            return 2.5;
    }
}
